import { FC, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import uuid from "uuid-random";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  Container,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { MeetingRoom, PauseCircleOutline, Close, Undo } from "@mui/icons-material";
import { ReEvent, EventSession, Id, Room, SessionStatus, ReSession } from "../../interfaces/types";
import RoomContainer from "./roomContainer";
import EventSessionContainer from "./eventSessionContainer";
import UnassignedSessionBox from "./unassignedSessionContainer";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useSessionContext } from "../../providers/SessionProvider";
import { useNavigate } from "react-router-dom";
dayjs.extend(duration);

// Define the props for the SessionScheduler component
interface SessionSchedulerProps {
  event: ReEvent;
}

// Define the SessionScheduler functional component
const SessionScheduler: FC<SessionSchedulerProps> = ({ event }) => {
  const navigate = useNavigate();
  // Calculate the number of days for the event
  const numberOfDays = Math.ceil(
    dayjs.duration(dayjs(event.endDate).diff(dayjs(event.startDate))).asDays()
  );
  // State to manage the rooms
  const [rooms, setRooms] = useState<Room[]>([
    {
      id: uuid(),
      name: "Room 1",
      capacity: 10,
    },
  ]);
  // Memoize the room IDs
  const roomIds = useMemo(() => rooms.map((room) => room.id), [rooms]);

  const { getSessionById } = useSessionContext();
  const [activeRoom, setActiveRoom] = useState<Room | null>(null);
  const [activeEventSession, setActiveEventSession] = useState<EventSession | null>(null);
  const [eventSessions, setEventSessions] = useState<EventSession[]>([]);

  useEffect(() => {
    if (event) {
      const selectedSessions = event.sessions.map((session) => getSessionById(session))
        .filter((session) => session?.status === SessionStatus.CONFIRMED) as ReSession[];
      setEventSessions(selectedSessions.map((session) => {
        return {
          id: session?.id || uuid(),
          orderId: 0,
          roomId: "unassigned",
          isBreak: false,
          isCopy: false,

          title: session?.title || "Untitled",
          duration: session?.duration || 0,
          startTime: new Date(),
          endTime: new Date(),
        };
      }));
    }
  }, [event, getSessionById]);

  const [openSnackBar, setOpenSnackBar] = useState(false);
  // State to manage the control key press status
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);

  // Effect to handle keydown and keyup events for the control key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Control") {
        setIsCtrlPressed(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Control") {
        setIsCtrlPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Function to handle closing the snackbar
  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };

  // Define the sensors for the drag and drop context
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  // Destructure the properties from useSortable hook
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({
      id: "UnassignedSessionBox",
      data: {
        type: "unassigned",
      },
    });

  // Define the style for the sortable item
  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  // Function to create a new room
  function createNewRoom() {
    const roomToAdd: Room = {
      id: uuid(),
      name: `Room ${rooms.length + 1}`,
      capacity: 10,
    };
    setRooms([...rooms, roomToAdd]);
  }

  // Function to delete an event session from a room
  function deleteEventSessionFromRoom(eventSessionId: Id) {
    const eventSession = eventSessions?.find(
      (eventSession) => eventSession.id === eventSessionId
    );

    if (
      (eventSession?.isBreak || eventSession?.isCopy) &&
      eventSession?.roomId === "unassigned"
    ) {
      setEventSessions(
        eventSessions?.filter((session) => session.id !== eventSessionId)
      );
      return;
    }
    setEventSessions(
      eventSessions?.map((eventSession) => {
        if (eventSession.id !== eventSessionId) return eventSession;
        return { ...eventSession, roomId: "unassigned" };
      })
    );
  }

  // Function to delete a room
  function deleteRoom(roomId: Id) {
    if (rooms.length === 1) {
      setOpenSnackBar(true);
    } else {
      eventSessions
        .filter((eventSession) => eventSession.roomId === roomId)
        .forEach((eventSession) => {
          eventSession.roomId = "unassigned";
        });
      setRooms(rooms.filter((room) => room.id !== roomId));
    }
  }

  // Function to update the name of a room
  function updateRoomName(roomId: Id, name: string) {
    setRooms(
      rooms.map((room) => {
        if (room.id !== roomId) return room;
        return { ...room, name };
      })
    );
  }

  // Function to update the capacity of a room
  function updateRoomCapacity(roomId: Id, capacity: number) {
    setRooms(
      rooms.map((room) => {
        if (room.id !== roomId) return room;
        return { ...room, capacity };
      })
    );
  }

  // Function to update the title of an event session
  function updateSessionTitle(eventSessionId: Id, title: string) {
    setEventSessions(
      eventSessions.map((eventSession) => {
        if (eventSession.id !== eventSessionId) return eventSession;
        return { ...eventSession, title };
      })
    );
  }

  // Function to update the duration of an event session
  function updateSessionDuration(eventSessionId: Id, duration: number) {
    setEventSessions(
      eventSessions.map((eventSession) => {
        if (eventSession.id !== eventSessionId) return eventSession;
        return { ...eventSession, duration };
      })
    );
  }

  // Function to handle drag start event
  function onDragStart(event: DragStartEvent) {
    if (isCtrlPressed && event.active.data.current?.type === "EventSession") {
      const session = event.active.data.current?.eventSession;
      const newSession = { ...session, id: uuid(), isCopy: true }; // Create a new session with a unique id

      const updatedSessions = [...eventSessions, newSession].sort(
        (a, b) => a.orderId - b.orderId
      );
      setEventSessions(updatedSessions);
    }
    if (event.active.data.current?.type === "Room") {
      setActiveRoom(event.active.data.current?.room);
      return;
    }
    if (event.active.data.current?.type === "EventSession") {
      setActiveEventSession(event.active.data.current?.eventSession);
      return;
    }
  }

  // Function to handle drag end event
  function onDragEnd(event: DragEndEvent) {
    setActiveRoom(null);
    setActiveEventSession(null);
    const { active, over } = event;
    if (!over) return;
    const activeRoomId = active.id;
    const overRoomId = over.id;
    if (activeRoomId === overRoomId) return;

    if (active.data.current?.type === "EventSession") {
      setEventSessions((eventSessions) => {
        const updatedEventSessions = eventSessions.map((eventSession) => {
          if (eventSession.roomId === activeRoomId) {
            return {
              ...eventSession,
              orderId: eventSessions.indexOf(eventSession),
            };
          }
          return eventSession;
        });
        return updatedEventSessions;
      });
      return;
    }

    setRooms((rooms) => {
      const overRoomIndex = rooms.findIndex((room) => room.id === overRoomId);
      const activeRoomIndex = rooms.findIndex(
        (room) => room.id === activeRoomId
      );
      return arrayMove(rooms, activeRoomIndex, overRoomIndex);
    });
  }

  // Function to handle drag over event
  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    if (activeId === overId) return;

    const isActiveAEventSession = active.data.current?.type === "EventSession";
    const isOverAEventSession = over.data.current?.type === "EventSession";
    const isOverAUnassignedArea = over.data.current?.type === "unassigned";

    if (!isActiveAEventSession) return;

    // dropping a session over another session
    if (isActiveAEventSession && isOverAEventSession) {
      setEventSessions((eventSessions) => {
        const activeIndex = eventSessions.findIndex(
          (eventSession) => eventSession.id === activeId
        );
        const overIndex = eventSessions.findIndex(
          (eventSession) => eventSession.id === overId
        );

        eventSessions[activeIndex].roomId = eventSessions[overIndex].roomId;

        const updatedEventSessions = arrayMove(
          eventSessions,
          activeIndex,
          overIndex
        ).map((eventSession, index) => ({
          ...eventSession,
          orderId: index,
        }));

        return updatedEventSessions;
      });
    }

    // dropping a session over unassigned area
    if (isActiveAEventSession && isOverAUnassignedArea) {
      setEventSessions((eventSessions) => {
        const activeIndex = eventSessions.findIndex(
          (eventSession) => eventSession.id === activeId
        );
        const overIndex = eventSessions.findIndex(
          (eventSession) => eventSession.id === overId
        );

        eventSessions[activeIndex].roomId = "unassigned";

        const updatedEventSessions = arrayMove(
          eventSessions,
          activeIndex,
          overIndex
        ).map((eventSession, index) => ({
          ...eventSession,
          orderId: index,
        }));

        return updatedEventSessions;
      });
    }

    // dropping a session over a room
    const isOverARoom = over.data.current?.type === "Room";
    if (isActiveAEventSession && isOverARoom) {
      setEventSessions((eventSessions) => {
        const activeIndex = eventSessions.findIndex(
          (eventSession) => eventSession.id === activeId
        );
        eventSessions[activeIndex].roomId = overId;

        const updatedEventSessions = eventSessions?.map(
          (eventSession, index) => ({
            ...eventSession,
            orderId: index,
          })
        );

        return updatedEventSessions;
      });
    }
  }

  // Function to update the timings of the event sessions
  function updateTimings() {
    rooms.forEach((room) => {
      let roomEventSessions = eventSessions
        .filter((eventSession) => eventSession.roomId === room.id)
        .sort((a, b) => a.orderId - b.orderId);
      roomEventSessions.forEach((eventSession, index) => {
        eventSession.startTime =
          index === 0
            ? new Date(event.startDate || Date.now())
            : roomEventSessions[index - 1].endTime;
        eventSession.endTime = new Date(
          eventSession.startTime.getTime() /*+ eventSession.duration*/
        );
      });
    });
  }

  // Effect to update the timings when rooms or event sessions change
  useEffect(() => {
    updateTimings();
  }, [rooms, eventSessions]);

  // Function to handle drag over event and update timings
  function handleDragOver(event: DragOverEvent) {
    onDragOver(event);
    updateTimings();
  }

  // Function to handle drag end event and update timings
  function handleDragEnd(event: DragEndEvent) {
    onDragEnd(event);
    updateTimings();
  }

  // Function to create a break session
  function createBreakSession() {
    const breakSession: EventSession = {
      id: uuid(),
      orderId: 0,
      roomId: "unassigned",
      isBreak: true,
      isCopy: false,
      title: "Lunchbreak",
      duration: 45,
      startTime: new Date(),
      endTime: new Date(),
    };
    setEventSessions([...eventSessions, breakSession]);
  }

  return (
    <Container disableGutters>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <Box
          sx={{
            mt: 3,
            mb: 3,
            width: "100%",
            justifySelf: "center",
            textAlign: "center",
            display: "flex",
            flexDirection: "row",
            flex: 1,
          }}
        >
          <Box
            className="SessionScheduler"
            display={"flex"}
            flex={"70%"}
            flexWrap={"wrap"}
            justifyContent={"center"}
            gap={5}
          >
            <SortableContext items={roomIds}>
              {rooms.map((room) => (
                <RoomContainer
                  key={room.id}
                  room={room}
                  deleteRoom={deleteRoom}
                  updateRoomName={updateRoomName}
                  updateRoomCapacity={updateRoomCapacity}
                  deleteEventSessionFromRoom={deleteEventSessionFromRoom}
                  eventSessions={eventSessions.filter(
                    (eventSession) => eventSession.roomId === room.id
                  )}
                  updateSessionTitle={updateSessionTitle}
                  updateSessionDuration={updateSessionDuration}
                />
              ))}
            </SortableContext>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderLeft: "1px solid #000",
              flex: "30%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: "10%",
              }}
            ><Button
              className="ReturnToSessionslistButton"
              onClick={() => navigate('/sessionlist/' + event.id)}
              variant="contained"
              color="primary"
              sx={{ m: 1, maxWidth: "100%" }}
            >
                <Undo/> Return to sessions list
              </Button>
              <Button
                className="AddRoomButton"
                onClick={() => createNewRoom()}
                variant="contained"
                color="primary"
                sx={{ m: 1, maxWidth: "100%" }}
              >
                <MeetingRoom /> Add Room
              </Button>
              <Button
                className="AddBreakButton"
                onClick={() => createBreakSession()}
                variant="contained"
                color="primary"
                sx={{ m: 1, maxWidth: "100%" }}
              >
                <PauseCircleOutline /> Add Break
              </Button>
            </Box>
            <Box
              id="UnassignedSessionBox"
              ref={setNodeRef}
              style={style}
              {...attributes}
              {...listeners}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: "90%",
                flexGrow: 1,
                p: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h6">Unassigned Sessions</Typography>
              <UnassignedSessionBox
                eventSessions={eventSessions.filter(
                  (eventSession) => eventSession.roomId === "unassigned"
                )}
                deleteEventSessionFromRoom={deleteEventSessionFromRoom}
                updateSessionTitle={updateSessionTitle}
                updateSessionDuration={updateSessionDuration}
              />
            </Box>
          </Box>

          {createPortal(
            <DragOverlay>
              {activeRoom && (
                <RoomContainer
                  room={activeRoom}
                  deleteRoom={deleteRoom}
                  updateRoomName={updateRoomName}
                  updateRoomCapacity={updateRoomCapacity}
                  deleteEventSessionFromRoom={deleteEventSessionFromRoom}
                  eventSessions={eventSessions.filter(
                    (eventSession) => eventSession.roomId === activeRoom.id
                  )}
                  updateSessionTitle={updateSessionTitle}
                  updateSessionDuration={updateSessionDuration}
                />
              )}
              {activeEventSession && (
                <EventSessionContainer
                  eventSession={activeEventSession}
                  deleteEventSession={deleteEventSessionFromRoom}
                  updateSessionTitle={updateSessionTitle}
                  updateSessionDuration={updateSessionDuration}
                />
              )}
            </DragOverlay>,
            document.body
          )}
          <Snackbar
            open={openSnackBar}
            autoHideDuration={10000}
            onClose={handleCloseSnackBar}
            message="You can't delete the last room, you need at least one room."
            action={
              <IconButton
                size="small"
                aria-label="close"
                onClick={handleCloseSnackBar}
              >
                <Close fontSize="small" />
              </IconButton>
            }
          />
        </Box>
      </DndContext>
    </Container>
  );
};

export default SessionScheduler;
